import { useEffect, useState } from 'react'
import { format } from 'date-fns'
import { paperAPI } from '../../services/api'
import type { PaperActivityEvent } from '../../types'
import { parseApiDate } from '../../utils/signalExpiry'
import { timeAgo } from './EngineActivityFeed'
import TerminalPanel from './TerminalPanel'

export const PAPER_ACTIVITY_POLL_MS = 300_000
export const PAPER_ACTIVITY_LIMIT = 12

/** Why a run did not trade, in words rather than enum names. */
const OUTCOME_NOTE: Record<string, string> = {
  ok: '',
  no_data: 'no BTC data — no decision made',
  warming_up: 'warming up the 200-day average',
  halted: 'forward test stopped',
  duplicate: 'candle already processed',
}

const MARKERS: Record<string, string> = {
  active: 'bg-violet-400 ring-violet-400/20',
  idle: 'bg-amber-400 ring-amber-400/15',
  halted: 'bg-red-400 ring-red-400/20',
}

const markerOf = (event: PaperActivityEvent) => {
  if (event.outcome === 'halted') return 'halted'
  if (!event.regime_on) return 'idle'
  return event.entries > 0 || event.exits > 0 ? 'active' : 'idle'
}

/**
 * The paper engine's daily runs: whether the BTC filter was on, how many coins
 * were eligible, and what it opened or closed. One line per closed candle.
 */
const PaperActivityFeed = () => {
  const [events, setEvents] = useState<PaperActivityEvent[] | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let active = true
    const load = async () => {
      try {
        const rows = await paperAPI.activity(PAPER_ACTIVITY_LIMIT)
        if (!active) return
        setEvents(rows)
        setFailed(false)
      } catch (error) {
        if (!active) return
        console.error('Paper activity refresh failed:', error)
        setFailed(true)
      }
    }
    load()
    const timer = setInterval(load, PAPER_ACTIVITY_POLL_MS)
    return () => {
      active = false
      clearInterval(timer)
    }
  }, [])

  const now = Date.now()

  return (
    <TerminalPanel
      title="Paper engine activity"
      tone="violet"
      className="h-full"
      subtitle={
        events && (
          <p className="font-mono text-xs tabular-nums text-gray-500">
            {events.length} run{events.length === 1 ? '' : 's'} · one per closed daily candle
          </p>
        )
      }
    >
      {events == null ? (
        <p className="py-10 text-center text-sm text-gray-500">
          {failed ? 'Paper activity is unavailable right now.' : 'Loading activity…'}
        </p>
      ) : events.length === 0 ? (
        <div className="rounded-md border border-dashed border-terminal-border py-10 text-center">
          <p className="text-sm text-gray-300">No runs recorded yet</p>
          <p className="mt-1 text-xs text-gray-500">The engine runs daily at 00:15 UTC.</p>
        </div>
      ) : (
        <ol className="space-y-2.5" data-testid="paper-activity">
          {events.map((event) => {
            const at = parseApiDate(event.at)
            const marker = markerOf(event)
            const note = OUTCOME_NOTE[event.outcome] ?? event.outcome.replace(/_/g, ' ')
            return (
              <li key={`${event.candle_date}-${event.at}`} className="flex gap-2.5 text-xs leading-relaxed">
                <span
                  aria-hidden="true"
                  className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ring-4 ${MARKERS[marker]}`}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-gray-300">
                    <span className="font-mono text-gray-500">{format(at, 'MMM d')}</span>{' '}
                    <span className={event.regime_on ? 'text-emerald-400' : 'text-amber-400'}>
                      BTC filter {event.regime_on ? 'on' : 'off'}
                    </span>
                    {event.symbols_eligible > 0 && (
                      <>
                        {' · '}
                        <span className="font-mono text-gray-200">{event.symbols_eligible}</span> eligible
                      </>
                    )}
                    {(event.entries > 0 || event.exits > 0) && (
                      <>
                        {' · '}
                        <span className="font-mono font-semibold text-violet-300">
                          {event.entries} in / {event.exits} out
                        </span>
                      </>
                    )}
                    {note && <span className="text-amber-300"> · {note}</span>}
                  </p>
                  {event.skipped_no_cash > 0 && (
                    <p className="font-mono text-[11px] text-gray-500">
                      {event.skipped_no_cash} entr{event.skipped_no_cash === 1 ? 'y' : 'ies'} skipped — no cash
                    </p>
                  )}
                </div>
                <span className="shrink-0 font-mono text-[11px] text-gray-600">{timeAgo(now - at.getTime())}</span>
              </li>
            )
          })}
        </ol>
      )}
    </TerminalPanel>
  )
}

export default PaperActivityFeed
